import { createAction, createSlice } from '@reduxjs/toolkit';
import * as oPath from '../../../Lib/Object/Path/Mutable';
import { getSyncTargetDefaults } from '../../Core/legacyDataSync';
import { applySyncPatch, propertyRequiresSync } from '../../ReduxViewModels/DataSync';

const { reducer, actions: sliceActions } = createSlice({
  name: 'equipment',
  initialState: {
    // Equipment read-only state
    equipmentId: null,
    equipmentType: {},
    config: {},

    // Equipment sync state & tracking
    ...getSyncTargetDefaults(),

    // Equipment temporary state (doesn't get synced immediately)
    equipmentTemp: {},

    // Equipment Status
    status: {
      // different operation progress
      synchronising: false,
      importing: false,

      // calculated
      saving: false, // combination of any equipment-related saving activity
      ready: false, // equipment etc has been loaded once.
      dirty: false,
    },

    // Fetched status data (for equipment calibration status icons)
    // Stored by an id given by the "owner" (i.e. the field's id id NOT equipment id, as status varies by date).
    statuses: {},
    equipmentData: {},
  },
  reducers: {
    init: (state, { payload: { equipmentId } = {} }) => {
      state.equipmentId = equipmentId;
      Object.entries(getSyncTargetDefaults()).forEach(([key, val]) => (state[key] = val));
      state.status.ready = false;
      state.synchronising = true;
    },
    initResolve: (state, { payload: { equipment, equipmentType, config } = {} }) => {
      state.equipmentType = equipmentType;
      state.config = config;
      Object.entries(getSyncTargetDefaults(equipment)).forEach(([key, val]) => (state[key] = val));
      state.status.ready = true;
      state.synchronising = false;
    },
    initReject: (state) => {
      state.status.ready = true;
      state.status.synchronising = false;
    },
    synchronise: (state) => {
      state.status.synchronising = true;
    },
    synchroniseComplete: (state, { payload: { syncPatch } = {} }) => {
      state.status.synchronising = false;
      state.status.dirty = false;
      if (syncPatch) applySyncPatch(state, syncPatch);
    },
    import: (state) => {
      state.status.importing = true;
    },
    importResolve: (state) => {
      state.status.importing = false;
    },
    importReject: (state) => {
      state.status.importing = false;
    },
    setValue: (state, { payload: { name, value } = {} }) => {
      oPath.set(state, `syncModel.${name}`, value, true);
      state.status.dirty = propertyRequiresSync(state, name);
    },
    copyValue: (state, { payload: { source, destination } }) => {
      oPath.set(state, `syncModel.${destination}`, oPath.get(state, `syncModel.${source}`), true);
    },
    patchValues: (state, { payload: { name, keyValuePairs } = {} }) => {
      oPath.setMultiple(
        state,
        `syncModel.${name}`,
        Object.fromEntries(keyValuePairs.map((kvp) => [kvp.key, kvp.value])),
        true
      );
    },
    setTempStateValue: (state, { payload: { tempId, key, value } = {} }) => {
      oPath.createPathIfNotExists(state, `equipmentTemp.${tempId}`);
      let tempKeyValuePairs = oPath.get(state, `equipmentTemp.${tempId}`);
      if (!tempKeyValuePairs) {
        oPath.set(state, `equipmentTemp.${tempId}`, [{ key, value }], true);
      } else {
        let keyIndex = tempKeyValuePairs.findIndex((kvp) => kvp.key === key);
        if (keyIndex >= 0) tempKeyValuePairs[keyIndex] = { key, value };
        else tempKeyValuePairs.push({ key, value });
      }
    },
    setTempStateValues: (state, { payload: { tempId, keyValuePairs } = {} }) => {
      oPath.set(state, `equipmentTemp.${tempId}`, keyValuePairs, true);
    },
    cleanupTempState: (state, { payload: { tempId } = {} }) => {
      oPath.del(state, `equipmentTemp.${tempId}`);
    },
    patchStatus: (state, { payload: { status } = {} }) => {
      oPath.setMultiple(state, 'status', status);
    },
    addArrayEntry: (state, { payload: { index, name, data = {} } = {} }) => {
      oPath.createPathIfNotExists(state, `syncModel.${name}`);
      let oldValue = oPath.get(state, `syncModel.${name}`);

      // We may have phantom records that are placeholders for records we want to exist. When adding a new record we
      // want those phantomt records to be created as well, otherwise it will create the first record only
      const gapsToFill = Math.max((index ?? 0) - (oldValue ?? []).length, 0);
      if (!oldValue) oPath.set(state, `syncModel.${name}`, [...Array(gapsToFill).fill({}), data], true);
      else oldValue.splice(index, 0, ...Array(gapsToFill).fill({}), data);
    },
    removeArrayEntry: (state, { payload: { index, name } = {} }) => {
      oPath.createPathIfNotExists(state, `syncModel.${name}`);
      let oldValue = oPath.get(state, `syncModel.${name}`);
      if (!oldValue) oPath.set(state, `syncModel.${name}`, [], true);
      else oldValue.splice(index, 1);
    },
    initStatus: (state, { payload: { id, equipmentId, statusDate } = {} }) => {
      oPath.set(state, `statuses.${id}`, {
        equipmentId,
        statusDate,
        initialised: true,
        retrievingStatus: false,
        status: null,
      });
    },
    cleanupStatus: (state, { payload: { id } = {} }) => {
      oPath.del(state, `statuses.${id}`);
    },
    getStatus: (state, { payload: { id } = {} }) => {
      state.statuses[id].retrievingStatus = true;
    },

    getStatusResolve: (state, { payload: { id, result } = {} }) => {
      oPath.setMultiple(state, `statuses.${id}`, {
        retrievingStatus: false,
        status: result,
      });
    },
    getStatusReject: (state, { payload: { id } = {} }) => {
      state.statuses[id].retrievingStatus = false;
    },

    initEquipmentData: (state, { payload: { id, equipmentId } = {} }) => {
      oPath.set(state, `equipmentData.${id}`, {
        equipmentId,
        initialised: true,
        retrieving: false,
        data: null,
      });
    },
    cleanupEquipmentData: (state, { payload: { id } = {} }) => {
      oPath.del(state, `equipmentData.${id}`);
    },
    getEquipmentData: (state, { payload: { id } = {} }) => {
      state.equipmentData[id].retrieving = true;
    },
    getEquipmentDataResolve: (state, { payload: { id, equipment } = {} }) => {
      oPath.setMultiple(state, `equipmentData.${id}`, {
        retrieving: false,
        ...equipment,
      });
    },
    getEquipmentDataReject: (state, { payload: { id } = {} }) => {
      state.equipmentData[id].retrieving = false;
    },
  },
});

const persistTempState = createAction('equipment/persistTempState');

export { reducer };
export const actions = { ...sliceActions, persistTempState };
